import { useEffect, useRef } from 'react'

function setAttr(selector, attr, value) {
  const el = document.querySelector(selector)
  if (el && value != null) el.setAttribute(attr, value)
}

/**
 * Composant SEO réutilisable — manipule les meta tags via DOM.
 * Fonctionne en SPA pure (pas de SSR requis).
 * Pour le crawl Googlebot, les pages prioritaires ont un HTML statique dans public/.
 *
 * Props :
 *   title        — <title> + og:title + twitter:title (54 chars max recommandé)
 *   description  — meta description + og:description + twitter:description (150-160 chars)
 *   canonical    — URL canonique absolue (https://insightball.com/...)
 *   ogTitle      — override de title pour OG (optionnel)
 *   ogDescription— override de description pour OG (optionnel)
 *   ogImage      — URL absolue de l'image OG (défaut : og-image.png)
 *   ogUrl        — override de canonical pour og:url (optionnel)
 *   ogType       — "website" | "article" (défaut : "website")
 *   jsonLd       — objet JSON-LD Schema.org (injecté dans <head>)
 *   noindex      — true pour noindex,nofollow (pages privées)
 */
export default function SeoHead({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = 'https://insightball.com/og-image.png',
  ogImageAlt = 'Insightball · Rapport de match et stats football.',
  ogUrl,
  ogType = 'website',
  jsonLd,
  noindex = false,
}) {
  const scriptRef = useRef(null)
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : null

  useEffect(() => {
    const resolvedOgTitle = ogTitle ?? title
    const resolvedOgDesc  = ogDescription ?? description
    const resolvedOgUrl   = ogUrl ?? canonical

    if (title)       document.title = title
    if (description) setAttr('meta[name="description"]', 'content', description)
    if (canonical)   setAttr('link[rel="canonical"]', 'href', canonical)

    setAttr('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow')

    // Open Graph
    if (resolvedOgTitle) setAttr('meta[property="og:title"]',       'content', resolvedOgTitle)
    if (resolvedOgDesc)  setAttr('meta[property="og:description"]',  'content', resolvedOgDesc)
    if (resolvedOgUrl)   setAttr('meta[property="og:url"]',          'content', resolvedOgUrl)
    if (ogType)          setAttr('meta[property="og:type"]',          'content', ogType)
    if (ogImage) {
      setAttr('meta[property="og:image"]',        'content', ogImage)
      setAttr('meta[property="og:image:width"]',  'content', '1200')
      setAttr('meta[property="og:image:height"]', 'content', '630')
      setAttr('meta[property="og:image:alt"]',    'content', ogImageAlt)
    }

    // Twitter Card
    setAttr('meta[name="twitter:card"]',         'content', 'summary_large_image')
    if (resolvedOgTitle) setAttr('meta[name="twitter:title"]',       'content', resolvedOgTitle)
    if (resolvedOgDesc)  setAttr('meta[name="twitter:description"]',  'content', resolvedOgDesc)
    if (ogImage) {
      setAttr('meta[name="twitter:image"]',     'content', ogImage)
      setAttr('meta[name="twitter:image:alt"]', 'content', ogImageAlt)
    }

    // JSON-LD
    const prev = document.getElementById('page-jsonld')
    if (prev) prev.remove()

    if (jsonLdStr) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id   = 'page-jsonld'
      script.text = jsonLdStr
      document.head.appendChild(script)
      scriptRef.current = script
    }

    return () => {
      if (scriptRef.current) {
        scriptRef.current.remove()
        scriptRef.current = null
      }
    }
  }, [title, description, canonical, ogTitle, ogDescription, ogImage, ogImageAlt, ogUrl, ogType, noindex, jsonLdStr])

  return null
}
